import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, ExternalLink, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { carPath } from "@/lib/carUrl";

const VIEWING_STATUS_HU = {
  pending_seller: "Eladó válaszára vár",
  pending_buyer: "Vevő válaszára vár",
  confirmed: "Megerősítve",
  declined: "Elutasítva",
  declined_by_buyer: "Vevő elutasította",
  rescheduled: "Új időpont javaslat",
};

function ViewingsPanel() {
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/listing-viewings", { params: { status, limit: 100 } });
      setRows(r.data.viewings || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-electric animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        data-testid="admin-viewings-filter"
        className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white"
      >
        <option value="all">Minden státusz</option>
        <option value="pending_seller">Eladó válaszára vár</option>
        <option value="pending_buyer">Vevő válaszára vár</option>
        <option value="confirmed">Megerősítve</option>
      </select>
      {rows.length === 0 ? (
        <p className="text-slate-400 text-sm">Nincs megtekintés-időpont.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((v) => (
            <div key={v.id} data-testid={`admin-viewing-${v.id}`} className="glass rounded-2xl p-4 flex flex-wrap gap-4 justify-between">
              <div className="min-w-0">
                <div className="font-medium">
                  {v.car ? `${v.car.make} ${v.car.model} (${v.car.year})` : v.car_id}
                </div>
                <div className="text-sm text-slate-300 mt-1">{v.summary || v.proposed_time || "—"}</div>
                <div className="text-xs text-slate-500 mt-2">
                  Eladó: {v.seller?.email || "—"} · Vevő: {v.buyer?.email || "—"}
                </div>
                <div className="text-xs text-slate-500">
                  {v.created_at ? new Date(v.created_at).toLocaleString("hu-HU") : ""}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="text-[11px] uppercase tracking-wide px-2 py-1 rounded-full bg-electric/15 text-electric">
                  {VIEWING_STATUS_HU[v.status] || v.status}
                </span>
                {v.car && (
                  <a
                    href={carPath(v.car)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-electric inline-flex items-center gap-1"
                  >
                    Hirdetés <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AiConversationsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/listing-ai/conversations", { params: { limit: 50 } })
      .then((r) => !cancelled && setRows(r.data.conversations || []))
      .catch(() => !cancelled && setRows([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const openConv = async (c) => {
    setSelected(c);
    setMsgLoading(true);
    try {
      const r = await api.get(`/admin/listing-ai/conversations/${c.id}/messages`);
      setMessages(r.data.messages || []);
    } catch {
      setMessages([]);
    } finally {
      setMsgLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-electric animate-spin" /></div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-[320px]">
      <div className="space-y-2 max-h-[480px] overflow-y-auto">
        {rows.length === 0 ? (
          <p className="text-slate-400 text-sm">Még nincs AI chat.</p>
        ) : (
          rows.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`admin-ai-conv-${c.id}`}
              onClick={() => openConv(c)}
              className={`w-full text-left glass rounded-xl p-3 border transition-colors ${
                selected?.id === c.id ? "border-electric/50 bg-white/[0.05]" : "border-white/5 hover:bg-white/[0.03]"
              }`}
            >
              <div className="font-medium text-sm">{c.car_label}</div>
              <div className="text-xs text-slate-500 mt-1">
                {c.buyer?.email} ↔ {c.seller?.email}
              </div>
              {c.open_viewing && (
                <span className="inline-block mt-2 text-[10px] uppercase text-amber-300">Nyitott időpont</span>
              )}
              <p className="text-xs text-slate-400 mt-2 line-clamp-2">{c.last_ai_message || c.last_message}</p>
            </button>
          ))
        )}
      </div>
      <div className="glass rounded-2xl border border-white/10 p-4 flex flex-col max-h-[480px]">
        {!selected ? (
          <p className="text-slate-500 text-sm flex items-center gap-2 m-auto">
            <MessageSquare className="w-4 h-4" /> Válassz beszélgetést
          </p>
        ) : msgLoading ? (
          <Loader2 className="w-6 h-6 text-electric animate-spin m-auto" />
        ) : (
          <>
            <div className="text-sm font-medium mb-3 pb-2 border-b border-white/10">{selected.car_label}</div>
            <div className="flex-1 overflow-y-auto space-y-2 text-sm">
              {messages.map((m) => {
                const isAi = m.sender_id === "__ai_listing_assistant__";
                const isSys = m.kind === "system";
                return (
                  <div
                    key={m.id}
                    className={`rounded-lg px-3 py-2 ${
                      isSys ? "bg-white/[0.03] text-slate-400 text-xs italic"
                        : isAi ? "bg-electric/10 text-slate-200"
                          : "bg-white/[0.06] text-slate-300"
                    }`}
                  >
                    {isAi && <span className="text-[10px] uppercase text-electric block mb-1">AI</span>}
                    {m.text || (m.kind === "offer" ? `Ajánlat: ${m.offer_amount} Ft` : "—")}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function AdminListingSupport({ defaultTab = "viewings" }) {
  return (
    <Tabs defaultValue={defaultTab === "ai" ? "ai" : "viewings"} className="mt-2">
      <TabsList className="flex gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-1.5 mb-4">
        <TabsTrigger value="viewings" data-testid="tab-listing-viewings" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
          Megtekintések
        </TabsTrigger>
        <TabsTrigger value="ai" data-testid="tab-listing-ai" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
          AI chat
        </TabsTrigger>
      </TabsList>
      <TabsContent value="viewings">
        <ViewingsPanel />
      </TabsContent>
      <TabsContent value="ai">
        <AiConversationsPanel />
      </TabsContent>
    </Tabs>
  );
}
